import React, { createContext, useContext, useState, useEffect, useMemo } from 'react'
import * as AuthMethods from './AuthMethods'
import { UserModules } from 'types/modules'

const AuthMfaSecureContext = createContext<any>({})

export const useAuthMfaSecure = () => {
  const context = useContext(AuthMfaSecureContext)
  if (!context) {
    throw new Error('useAuthMfaSecure must be used within a AuthMfaSecureProvider')
  }
  return context
}

export const AuthMfaSecureProvider = ({ children }) => {
  const [tempUser, setTempUser] = useState(null)
  const [isFullyAuthenticated, setIsFullyAuthenticated] = useState(false)
  const [currentSession, setCurrentSession] = useState(null)
  const [currentUser, setCurrentUser] = useState(null)
  const [userModules, setUserModules] = useState(null)


  const secureSignIn = async (email, password) => {
    try {
      const authData = await AuthMethods.signInWithPassword(email, password)

      setTempUser(authData.user)
      setCurrentSession(authData.session)
      setIsFullyAuthenticated(false)
      console.log('🔐 Login temporal exitoso - esperando verificación MFA')
      return authData
    } catch (error) {
      setTempUser(null)
      setIsFullyAuthenticated(false)
      throw error
    }
  }

  const verifyChallengeMFA = async (factorId, code, challengeId = null) => {
    try {
      const mfaData = await AuthMethods.verifyMFA(factorId, code, challengeId)
      console.log('✅ MFA verificado - estableciendo sesión completa')
      setIsFullyAuthenticated(true)
      setCurrentUser(tempUser)
      return mfaData
    } catch (error) {
      console.error('❌ Error en MFA:', error)
      setIsFullyAuthenticated(false)
      throw error
    }
  }

  // Wrapper methods usando AuthMethods
  const createChallengeMFA = AuthMethods.createMFAChallenge
  const getAuthenticatorAssuranceLevel = AuthMethods.getAuthenticatorAssuranceLevel
  const listFactorsMFA = AuthMethods.getMFAFactors
  const enrollMFA = async (friendlyName) => {
    let finalName = friendlyName
    if (!finalName) {
      finalName = AuthMethods.generateRandomDeviceName()
    }

    return await AuthMethods.enrollMFA(finalName)
  }

  const getCurrentSession = async (forceRefresh = false) => {
    try {
      if (currentSession && !forceRefresh) return currentSession
      const session = await AuthMethods.getSession()
      setCurrentSession(session)
      return session
    } catch (error) {
      console.error('Error obteniendo sesión:', error)
      return null
    }
  }

  const getCurrentUser = async () => {
    try {
      const user = await AuthMethods.getUser()
      setCurrentUser(user)
      return user
    } catch (error) {
      console.error('❌ Error obteniendo usuario:', error)
      return null
    }
  }

  const secureSignOut = async () => {
    await AuthMethods.signOut()
    setTempUser(null)
    setCurrentUser(null)
    setCurrentSession(null)
    setIsFullyAuthenticated(false)
  }


  const listModules = async () => {
    if (userModules) return userModules
    
    let userId = currentUser?.id;
    if (!userId) {
      try {
        const user = await AuthMethods.getUser();
        userId = user?.id;
        if (user) {
          setCurrentUser(user);
        }
      } catch (error) {
        console.error('AuthMfaSecureManage: Error getting user', error);
      }
    }
    
    if (!userId) {
      console.error('AuthMfaSecureManage: No user ID available');
      return [];
    }
    
    const { data: userModulesWithDetails, error } = await AuthMethods.query
    .from('user_modules')
    .select(`
      can_read,
      can_write,
      can_delete,
      partner:partners (
        id,
        name,
        logo_url,
        numeric_id
      ),
      modules (
        id,
        type,
        name
      )
    `)
    .eq('user_id', userId)
    .eq('is_active', true);
        
    const uniqueModules = userModulesWithDetails?.reduce((acc: any[], current: any) => {
      const exists = acc.find(item => item.modules?.id === current.modules?.id);
      if (!exists) {
        acc.push(current);
      }
      return acc;
    }, []) || [];
    
    
    if (error) {
      console.error('AuthMfaSecureManage: Error fetching modules', error);
      return [];
    }
    
    setUserModules(uniqueModules)
    return uniqueModules;
  }

  const value = useMemo(() => ({
    isFullyAuthenticated,
    currentSession,
    currentUser,
    tempUser,
    // Métodos de autenticación
    secureSignIn,
    secureSignOut,
    verifyChallengeMFA,
    createChallengeMFA,
    enrollMFA,
    listFactorsMFA,
    getAuthenticatorAssuranceLevel,
    // Session
    getCurrentSession,
    getCurrentUser,
    //Querys
    listModules,
  }), [
    isFullyAuthenticated,
    currentSession,
    currentUser,
    tempUser,
    secureSignIn,
    secureSignOut,
    verifyChallengeMFA,
    createChallengeMFA,
    enrollMFA,
    listFactorsMFA,
    getAuthenticatorAssuranceLevel,
    getCurrentSession,
    getCurrentUser,
    listModules
  ])

  return (
    <AuthMfaSecureContext.Provider value={value}>
      {children}
    </AuthMfaSecureContext.Provider>
  )
}